import axios from 'axios';
import BaseAPIs from "./base.apis";
import {iChatSession, iCollectionElement, iContact, iShopifyReview, iShopifyShop, iUser} from "../types/internal";
import {iApiBasicResponse, iBasicListingResponse, iListingQuery, iPlan} from "../types/api";
import { iOnboardingFlags, iOnboardingFlagsResponse } from '../types/internal';
import {iEmailCampaign} from "../types/internal/email/campaign";
import {ObjectFlatter} from "./resource.apis";
import {removeToken} from "../push-notification";


export interface iUserLoginResponse extends iApiBasicResponse {
    authenticated?: boolean
}

export interface iFetchUserResponse extends iApiBasicResponse {
    user?: iUser,
    plans?: iPlan[],
    shops?: iShopifyShop[]
    redirectURL?: string
    shop_membership?: {
        is_owner?: boolean;
        shop_role?: { slug: string; name: string; id?: number } | null;
        permissions?: Record<string, string>;
    }
}

export interface iSignature extends iCollectionElement {

    designation: string
    full_name: string
    logo_url: string
    logo: string
    phone: string
    skype: string
    website: string
    facebook: string
    instagram: string
    linkedin: string
    twitter: string
    email: string
}

export interface iFetchContactResponse extends iApiBasicResponse {
    contact?: iContact
}

export interface iAccountLanguage {
    id: number;
    uid: string;
    code: string;
    name: string;
}

export interface iAccountLanguagesResponse extends iApiBasicResponse {
    languages?: iAccountLanguage[];
}

export interface iUpdateAccountLanguageParams {
    language?: string;
    language_id?: string | number;
}

export interface iUpdateAccountLanguageResponse extends iApiBasicResponse {
    language_id?: number;
    language?: iAccountLanguage;
    widget_copy?: iWidgetCopyMaps;
}

export interface iWidgetCopyMaps {
    reviews?: Record<string, string>;
    chats?: Record<string, string>;
}

export interface iWidgetCopyResponse extends iApiBasicResponse {
    locale?: string;
    reviews?: Record<string, string>;
    chats?: Record<string, string>;
}

export interface iProfileUpdateParams {
    image?: any
    _remove_image?: string
    first_name: string
    last_name: string
    timezone: string
    language_id?: string
    color_scheme?: string
    email: string
    password: string
    password_confirmation: string
}

export interface iContactUpdateParams {
    first_name: string
    last_name: string
    email: string
    address_1: string
    city: string
    zip: string
    url: string
    country_id: string
    company: string
    phone: string
    address_2: string
    state: string
    tax_number: string
    billing_address: string
}

export interface AnalyticsData {
    total_price: string,
    date: string
    /** Present on daily order report buckets from `/report` */
    number_of_orders?: number | string
}

export interface iSubscriberGraphData {
    number_of_subscribers: string,
    date: string
}

export interface iReviewGraphData {
    number_of_reviews: string,
    date: string
}

export interface DashboardHomeResponse extends iApiBasicResponse {
    recent_chats: iChatSession[],
    recent_reviews: iShopifyReview[],
}

export interface iDashboardReportResponse extends iApiBasicResponse {
    revenue_report: iRevenueReport,
    subscribers: {
        items: iSubscriberGraphData[],
        total_subscriber: number
    },
    reviews: {
        items: iReviewGraphData[],
        total_reviews: number
        total_reviews_within_timeframe: number
        average_rating?: number
    },
    emails?: EmailStats
    shopify_orders: iAnalyticsDataWrapper
    emailwish_shopify_orders: iAnalyticsDataWrapper
    sales_breakdown?: SalesChannels
}
export interface iDashboardPopupSummaryResponse extends iApiBasicResponse {
    popup_summary:{
        impressions: number
        impressions_change_last_24: number
        impressions_last_24_hours: number
        impressions_last_48_to_24_hours: number
        impressions_series?: number[]
        number_of_sales: number
        number_of_sales_48_to_24_hours: number
        number_of_sales_change_last_24: number
        number_of_sales_last_24_hours: number
        responses: number
        responses_change_last_24: number
        responses_last_24_hours: number
        responses_last_48_to_24_hours: number
        responses_series?: number[]
        sales_total: number
        sales_total_change_last_24: number
        sales_total_last_24_hours: number
        sales_total_last_48_to_24_hours: number
        sales_total_series?: number[]
    }
}

export interface iRevenueReport {
    cost_change_last_24: number
    cost_last_24_hours: number
    cost_last_48_to_24_hours: number
    cost_total: number
    count_change_last_24: number
    count_last_24_hours: number
    count_last_48_to_24_hours: number
    count_total: number
    revenue_change_last_24: number
    revenue_last_24_hours: number
    revenue_last_48_to_24_hours: number
    revenue_total: number
    roi_change_last_24: number
    roi_last_24_hours: number
    roi_last_48_to_24_hours: number
    roi_total: number
}

export interface iAnalyticsDataWrapper {
    total_sales: number,
    total_orders: number,
    items: AnalyticsData[]
}

export interface iDashboardHomeAnalytics extends iApiBasicResponse {
    shopify_orders: iAnalyticsDataWrapper
    emailwish_shopify_orders: iAnalyticsDataWrapper
}

export interface ModuleSettings extends iApiBasicResponse {
    chat_module_enabled?: boolean
    popup_module_enabled?: boolean
    review_module_enabled?: boolean
    theme_embed_enabled?: boolean
    theme_embed_required?: boolean
    theme_embed_setup_complete?: boolean
    theme_embed_setup_acknowledged?: boolean
    theme_embed_url?: string
    legacy_script_tag_active?: boolean
    shopify_scripts_sunset_date?: string
    shopify_scripts_edit_frozen_date?: string
    wishlist_module_enabled?: boolean
    customer_account_wishlist_enabled?: boolean
    customer_account_wishlist_setup_url?: string
    customer_account_wishlist_page_url?: string
}

export interface WishlistSettings extends iApiBasicResponse {
    wishlist_module_enabled?: boolean
    customer_account_wishlist_enabled?: boolean
    customer_account_wishlist_setup_url?: string
    customer_account_wishlist_page_url?: string
}

export interface PopupSettings extends iApiBasicResponse {
    popup_module_enabled?: boolean
}

export interface ReviewSettings extends iApiBasicResponse {
    review_module_enabled?: boolean
}

export interface ChatSettings extends iApiBasicResponse {
    chat_module_enabled?: boolean
}

export interface SalesChannels {
    number_of_sales:number
    number_of_sales_from_chats: number
    number_of_sales_from_emails: number
    number_of_sales_from_emailwish: number
    number_of_sales_from_popups: number
    sales_total: number
    sales_total_from_chats: number
    sales_total_from_emails: number
    sales_total_from_emailwish: number
    sales_total_from_popups:number
}

export interface SalesBreakDownResponse extends iApiBasicResponse {
    campaigns: iBasicListingResponse<iEmailCampaign>,
    sales_channels?: SalesChannels
}

export interface EmailStats extends iApiBasicResponse {
    clicked_email: number
    delivered_email: number
    opened_email: number
    total_emails: number
}

export interface EmailStatsResponse extends iApiBasicResponse {
    emails?: EmailStats
}

export interface iSignatureResponse extends iApiBasicResponse {
    signature?: iSignature
}

export interface iUpdateEmailContentParams {
    header?: string;
    footer?: string;
    primary_background_color?: string;
    primary_text_color?: string;
    secondary_background_color?: string;
    secondary_text_color?: string;
    canvas_background_color?: string;
    canvas_text_color?: string;
    font_family?: string;
}

export interface iSubscriberGraphResponse extends iApiBasicResponse {
    subscriber: {
        items: iSubscriberGraphData[],
        total_subscriber: number
    }
}

export interface iReviewStatsResponse extends iApiBasicResponse {
    reviews: {
        items: iReviewGraphData[],
        total_reviews: number
        total_reviews_within_timeframe: number
    }
}

export interface iPermission extends iApiBasicResponse {
    unsubscribe_required: boolean,
    access_offline: boolean,
    import: boolean,
    export: boolean,
    hide_branding: boolean,
    api: boolean
}

/** Coalesce GET /signature by active shop (customer UID header). Survives StrictMode remounts. */
let signatureFetchInflight: Promise<iSignatureResponse> | null = null;
let signatureFetchKey: string | null = null;
let signatureFetchCached: iSignatureResponse | null = null;

function signatureShopKey(): string {
    const headers = axios.defaults.headers as Record<string, unknown>;
    const uid = headers["X-Emailwish-Customer-UID"];
    return typeof uid === "string" && uid.length > 0 ? uid : "";
}

export function invalidateUserSignatureCache(): void {
    signatureFetchInflight = null;
    signatureFetchKey = null;
    signatureFetchCached = null;
}

export function hasCachedUserSignature(): boolean {
    return signatureFetchCached != null && signatureFetchKey === signatureShopKey();
}

export function getCachedUserSignature(): iSignatureResponse | null {
    return hasCachedUserSignature() ? signatureFetchCached : null;
}

export default class UserAPIs extends BaseAPIs {


    fcm_token = async (token: string): Promise<iUserLoginResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("token", token);
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/fcm_token", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): iUserLoginResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    login = async (email: string, password: string): Promise<iUserLoginResponse> => {
        removeToken().then();
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("email", email);
        fd.set("password", password);
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/login", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): iUserLoginResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    popup_module_status = async (enabled: boolean): Promise<PopupSettings> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("action", enabled ? "install" : "uninstall");
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/popup_module_status", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): PopupSettings => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    review_module_status = async (enabled: boolean): Promise<ReviewSettings> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("action", enabled ? "install" : "uninstall");
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/review_module_status", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): iUserLoginResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    wishlist_module_status = async (enabled?: boolean): Promise<WishlistSettings> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        if (enabled === undefined) {
            return axios
                .get(this.getApiBaseURL() + "/wishlist_module_status", {
                    withCredentials: true,
                })
                .then((res): WishlistSettings => ({statusCode: res.status, ...res.data}))
                .catch((error): any => {
                    return this.handleCatch(error);
                });
        }

        let fd = new FormData();
        fd.set("action", enabled ? "install" : "uninstall");
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/wishlist_module_status", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): WishlistSettings => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    customer_account_wishlist_status = async (enabled?: boolean): Promise<WishlistSettings> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        if (enabled === undefined) {
            return axios
                .get(this.getApiBaseURL() + "/customer_account_wishlist_status", {
                    withCredentials: true,
                })
                .then((res): WishlistSettings => ({statusCode: res.status, ...res.data}))
                .catch((error): any => {
                    return this.handleCatch(error);
                });
        }

        let fd = new FormData();
        fd.set("action", enabled ? "install" : "uninstall");
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/customer_account_wishlist_status", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): WishlistSettings => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    chat_module_status = async (enabled: boolean): Promise<ChatSettings> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("action", enabled ? "install" : "uninstall");
        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/chat_module_status", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): ChatSettings => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    logout = async (): Promise<iApiBasicResponse> => {
        removeToken().then();
        return axios
            .get(this.getApiBaseURL() + "/logout", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    modules_status = async (refresh = false, acknowledge = false): Promise<ModuleSettings> => {
        return axios
            .get(this.getApiBaseURL() + "/modules_status", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime(),
                    ...(refresh ? { refresh: 1 } : {}),
                    ...(acknowledge ? { acknowledge: 1 } : {}),
                }
            })
            .then((res): ModuleSettings => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    fetch_user = async (): Promise<iFetchUserResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/current_user", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): iFetchUserResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    // Add debug logging to see what's happening
    // update the signature to match the shape you actually pass (pick the right interface)
    setOnboardingFlags = async (flagsData: iOnboardingFlags /* or iOnboardingFlagsAPI */): Promise<iOnboardingFlagsResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return { statusCode: 999, errors: { network: "Network error!" } };
        let profile_data : any = flagsData;
        // getting error Full error details: TypeError: Converting circular structure to JSON
    // --> starting at object with constructor 'HTMLSpanElement'
    // |     property '__reactFiber$dt3pcnovgdg' -> object with constructor 'FiberNode'
    // --- property 'stateNode' closes the circle
    // at JSON.stringify (<anonymous>)

        const fd = new FormData();
        fd.append("_token", csrfToken);

        // Keys that must be sent as raw strings (not coerced to 1/0)
        // const keysAsStrings = new Set([
        //     "onboarding_step_3",
        //     "onboarding_abandoned_cart2_step_5",
        //     "onboarding_abandoned_cart3_step_5",
        //     "onboarding_step_7_email",
        //     "onboarding_step_2_slider_1",
        //     "onboarding_step_2_slider_2",
        // ]);
        // Object.entries(flagsData).forEach(([key, value]) => {
        //     if (keysAsStrings.has(key)) {
        //     // Preserve the actual string / numeric value (fallback to "0" if null/undefined)
        //     fd.append(`profile_setting[${key}]`, value == null ? "0" : String(value));
        //     } else {
        //     // For boolean flags: normalize into "1" or "0"
        //     // Accept booleans or previously-stringified "1"/1 as true
        //     const truthy = value === true || value === "1" || value === 1;
        //     fd.append(`profile_setting[${key}]`, truthy ? "1" : "0");
        //     }
        // });

        return axios
            .post(this.getApiBaseURL() + "/onboarding/flags", { 
                'profile_setting': profile_data,
                '_token': csrfToken
             }, {
                withCredentials: true,
                params: { ts: new Date().getTime() }
            })
            .then((res): iOnboardingFlagsResponse => ({ statusCode: res.status, ...res.data }))
            .catch((error): any => {
            console.log("Full error details:", error);
            return this.handleCatch(error);
        });
    };

    // sent_test_email = async (recipient: string): Promise<iOnboardingFlagsResponse> => {
    //     const content = `<div style="background-size: cover; background-repeat: no-repeat; background-color: rgb(245, 245, 245); " class="container_bg" > <div class="container" style="width: 640px; padding-bottom: 0px; background-size: cover; background-repeat: no-repeat; background-color: rgb(255, 255, 255); "> <div class="row clearfix" style="max-width: 100%;"><div class="column full" style="max-width: 100%;"><div style="width: 100%; max-width: 100%; text-align: left;"><img src="https://builder.emailwish.com/cb/assets/minimalist-blocks/images/logoplaceholder.png" style="max-width: 100%; width: 150px;" alt="Logo"></div></div></div><div class="row clearfix" style="max-width: 100%;"><grammarly-extension data-grammarly-shadow-root="true" style="position: absolute; top: 0px; left: 0px; pointer-events: none; max-width: 100%;" class="cGcvT"></grammarly-extension><grammarly-extension data-grammarly-shadow-root="true" style="mix-blend-mode: darken; position: absolute; top: 0px; left: 0px; pointer-events: none; max-width: 100%;" class="cGcvT"></grammarly-extension><div class="column full" style="max-width: 100%;" spellcheck="false" autocomplete="off"><p style="max-width: 100%; font-size: 15px; line-height: 27px;">Hey {SUBSCRIBER_FIRST_NAME},&nbsp;</p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"></p><p style="max-width: 100%;"><span style="font-size: 15px; max-width: 100%;">Thank you so much for placing your order! This means a lot to us. We will try our best to provide you with the best customer experience.&nbsp;</span><br style="max-width: 100%;"></p><p style="max-width: 100%;"></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"></p><p style="line-height: 27px; font-size: 15px; max-width: 100%;">I will update you again with further details when the order is shipped. Looking forward to serving you again.&nbsp;</p><span style="font-size: 15px; max-width: 100%;">Cheers,<br style="max-width: 100%;">Ankit test<br style="max-width: 100%;">Founder<br style="max-width: 100%;">amitsrivastava0191@gmail.com</span><p style="max-width: 100%;"></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"><span style="font-size: 15px; max-width: 100%;"><br style="max-width: 100%;"><i style="font-size: 12px; max-width: 100%;"><b style="max-width: 100%;">P.S&nbsp;-&nbsp;</b>To ensure delivery to your inbox instead of your junk mail folder, please add amitsrivastava0191@gmail.com to your address book.</i><br style="max-width: 100%;"></span></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"><span style="font-size: 15px; max-width: 100%;"><i style="font-size: 12px; max-width: 100%;"><br style="max-width: 100%;"></i></span></p><p style="max-width: 100%;"></p><div class="inner-row" style="max-width: 100%;"><p class="dragme" style="max-width: 100%; float: none; font-size: 12px;text-align:center;">asadasdankit.myshopify.com</p><p class="dragme" style="max-width: 100%; float: none; font-size: 12px;text-align:center;">sas,bhatkote road,near siltham, b 1398 indira ngr, pithoragarh, Uttarakhand</p><div style="margin: 1em 0px; text-align: center; max-width: 100%;" class="is-rounded-button-medium social-media-buttons-holder social-buttons-container" data-subblock="true"><span style="max-width: 100%;"><a class="social-button-link" data-title="Twitter" href="" style="max-width: 100%;" id="gbtlxKE"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/tw-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/twitter-1.png" style="margin: 0px; max-width: 100%;"></a></span><span style="max-width: 100%;"><a class="social-button-link" data-title="Facebook" href="https://www.facebook.com/EmailWish" style="max-width: 100%;" id="iszkAvv"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/fb-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/facebook-1.png" style="margin: 0px; max-width: 100%;"></a></span><span style="max-width: 100%;"><a class="social-button-link" data-title="Instagram" href="https://www.instagram.com/emailwish/" style="max-width: 100%;" id="AnDtsAZ"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/insta-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/instagram-1.png" style="margin: 0px; max-width: 100%;"></a></span></div>&nbsp;<p style="max-width: 100%;"></p></div></div></div> </div> </div>`
    //     return axios
    //         .post(this.getApiBaseURL() + "/ew_dynamic/send_email", { recipient, content }, {
    //             withCredentials: true,
    //             params: { ts: new Date().getTime() }
    //         })
    //         .then((res): iOnboardingFlagsResponse => ({ statusCode: res.status, ...res.data }))
    //         .catch((error): any => {
    //             return this.handleCatch(error);
    //     });
    // }
    sent_test_email = async (recipient: string): Promise<iOnboardingFlagsResponse> => {
        const content = `<div style="background-size: cover; background-repeat: no-repeat; background-color: rgb(245, 245, 245); " class="container_bg" > <div class="container" style="width: 640px; padding-bottom: 0px; background-size: cover; background-repeat: no-repeat; background-color: rgb(255, 255, 255); "> <div class="row clearfix" style="max-width: 100%;"><div class="column full" style="max-width: 100%;"><div style="width: 100%; max-width: 100%; text-align: left;"><img src="https://builder.emailwish.com/cb/assets/minimalist-blocks/images/logoplaceholder.png" style="max-width: 100%; width: 150px;" alt="Logo"></div></div></div><div class="row clearfix" style="max-width: 100%;"><grammarly-extension data-grammarly-shadow-root="true" style="position: absolute; top: 0px; left: 0px; pointer-events: none; max-width: 100%;" class="cGcvT"></grammarly-extension><grammarly-extension data-grammarly-shadow-root="true" style="mix-blend-mode: darken; position: absolute; top: 0px; left: 0px; pointer-events: none; max-width: 100%;" class="cGcvT"></grammarly-extension><div class="column full" style="max-width: 100%;" spellcheck="false" autocomplete="off"><p style="max-width: 100%; font-size: 15px; line-height: 27px;">Hey {SUBSCRIBER_FIRST_NAME},&nbsp;</p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"></p><p style="max-width: 100%;"><span style="font-size: 15px; max-width: 100%;">Thank you so much for placing your order! This means a lot to us. We will try our best to provide you with the best customer experience.&nbsp;</span><br style="max-width: 100%;"></p><p style="max-width: 100%;"></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"></p><p style="line-height: 27px; font-size: 15px; max-width: 100%;">I will update you again with further details when the order is shipped. Looking forward to serving you again.&nbsp;</p><span style="font-size: 15px; max-width: 100%;">Cheers,<br style="max-width: 100%;">Ankit test<br style="max-width: 100%;">Founder<br style="max-width: 100%;">amitsrivastava0191@gmail.com</span><p style="max-width: 100%;"></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"><span style="font-size: 15px; max-width: 100%;"><br style="max-width: 100%;"><i style="font-size: 12px; max-width: 100%;"><b style="max-width: 100%;">P.S&nbsp;-&nbsp;</b>To ensure delivery to your inbox instead of your junk mail folder, please add amitsrivastava0191@gmail.com to your address book.</i><br style="max-width: 100%;"></span></p><p style="max-width: 100%; font-size: 15px; line-height: 27px;"><span style="font-size: 15px; max-width: 100%;"><i style="font-size: 12px; max-width: 100%;"><br style="max-width: 100%;"></i></span></p><p style="max-width: 100%;"></p><div class="inner-row" style="max-width: 100%;"><p class="dragme" style="max-width: 100%; float: none; font-size: 12px;text-align:center;">asadasdankit.myshopify.com</p><p class="dragme" style="max-width: 100%; float: none; font-size: 12px;text-align:center;">sas,bhatkote road,near siltham, b 1398 indira ngr, pithoragarh, Uttarakhand</p><div style="margin: 1em 0px; text-align: center; max-width: 100%;" class="is-rounded-button-medium social-media-buttons-holder social-buttons-container" data-subblock="true"><span style="max-width: 100%;"><a class="social-button-link" data-title="Twitter" href="" style="max-width: 100%;" id="gbtlxKE"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/tw-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/twitter-1.png" style="margin: 0px; max-width: 100%;"></a></span><span style="max-width: 100%;"><a class="social-button-link" data-title="Facebook" href="https://www.facebook.com/EmailWish" style="max-width: 100%;" id="iszkAvv"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/fb-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/facebook-1.png" style="margin: 0px; max-width: 100%;"></a></span><span style="max-width: 100%;"><a class="social-button-link" data-title="Instagram" href="https://www.instagram.com/emailwish/" style="max-width: 100%;" id="AnDtsAZ"><img class="social-button-icon no-image-edit" data-bg="#000000" data-icon="#ffffff" data-bgnew="#000000ff" data-iconnew="#ffffffff" data-iconsrc="https://builder.emailwish.com/cb/assets/social-icons/small/insta-round.png" src="https://builder.emailwish.com/storage/social_images/email/6257626f3826b/instagram-1.png" style="margin: 0px; max-width: 100%;"></a></span></div>&nbsp;<p style="max-width: 100%;"></p></div></div></div> </div> </div>`
        return axios
            .post(this.getApiBaseURL() + "/onboarding-test-mail", { recipient, content }, {
                withCredentials: true,
                params: { ts: new Date().getTime() }
            })
            .then((res): iOnboardingFlagsResponse => ({ statusCode: res.status, ...res.data }))
            .catch((error): any => {
                return this.handleCatch(error);
        });
    }

    send_plain_email = async (params: {
        subject: string;
        to_email: string;
        from_email: string;
        plain: string;
    }): Promise<iApiBasicResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return { statusCode: 999, errors: { network: "Network error!" } };

        const fd = new FormData();
        fd.append("_token", csrfToken);
        fd.append("subject", params.subject);
        fd.append("to_email", params.to_email);
        fd.append("from_email", params.from_email);
        fd.append("plain", params.plain);

        return axios
            .post(this.getApiBaseURL() + "/onboarding-test-mail", fd, {
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({ statusCode: res.status, ...res.data }))
            .catch((error): any => this.handleCatch(error));
    };

    fetch_user_signature = async (): Promise<iSignatureResponse> => {
        const key = signatureShopKey();
        if (signatureFetchCached && signatureFetchKey === key) {
            return signatureFetchCached;
        }
        if (signatureFetchInflight && signatureFetchKey === key) {
            return signatureFetchInflight;
        }
        signatureFetchKey = key;
        signatureFetchInflight = axios
            .get(this.getApiBaseURL() + "/signature", {
                withCredentials: true,
            })
            .then((res): iSignatureResponse => {
                const data = {statusCode: res.status, ...res.data};
                if (signatureFetchKey === key) {
                    signatureFetchCached = data;
                }
                return data;
            })
            .catch((error): any => {
                return this.handleCatch(error);
            })
            .finally(() => {
                if (signatureFetchKey === key) {
                    signatureFetchInflight = null;
                }
            });
        return signatureFetchInflight;
    };

    delete_user_signature = async (): Promise<iSignatureResponse> => {
        invalidateUserSignatureCache();
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};
        let fd = new FormData();
        fd.set("_token", csrfToken);
        fd.set("_method", "DELETE");
        return axios
            .post(this.getApiBaseURL() + "/signature/image",fd, {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): iSignatureResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    update_user_signature = async (values: any): Promise<iSignatureResponse> => {
        invalidateUserSignatureCache();
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        let flattenedParams = new ObjectFlatter(values);
        flattenedParams.get_form_data().forEach(entry => {
            fd.set(entry.key, entry.value);
        });
        fd.delete("logo");
        if (values.logo instanceof File) {
            fd.set("logo", values.logo);
        } else if (typeof values.logo === 'string' && values.logo.trim() !== "" && values.logo !== "undefined" && values.logo !== "null") {
            // Only send if it's a valid URL or data string, not a placeholder
            fd.set("logo", values.logo);
        }

        fd.set("_token", csrfToken);
        return axios
            .post(this.getApiBaseURL() + "/signature", fd, {
                withCredentials: true,
            })
            .then((res): iSignatureResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    update_email_content = async (
        params: iUpdateEmailContentParams
    ): Promise<iApiBasicResponse> => {
        const csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return { statusCode: 999, errors: { network: "Network error!" } };

        const fd = new FormData();
        fd.set("_token", csrfToken);
        (Object.keys(params) as (keyof iUpdateEmailContentParams)[]).forEach((key) => {
            const value = params[key];
            if (value !== undefined && value !== null) {
                fd.set(key, String(value));
            }
        });

        return axios
            .post(this.getApiBaseURL() + "/signature/update_email_content", fd, {
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({ statusCode: res.status, ...res.data }))
            .catch((error): any => this.handleCatch(error));
    };

    fetch_contact = async (): Promise<iFetchContactResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/account/contact", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): iFetchContactResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    load_account_languages = async (): Promise<iAccountLanguagesResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/account/languages", {
                withCredentials: true,
                params: { ts: new Date().getTime() },
            })
            .then((res): iAccountLanguagesResponse => ({
                statusCode: res.status,
                ...res.data,
            }))
            .catch((error): any => this.handleCatch(error));
    };

    /** Persist display locale for the logged-in account (builder `POST /account/language`). */
    update_account_language = async (
        params: iUpdateAccountLanguageParams
    ): Promise<iUpdateAccountLanguageResponse> => {
        const payload: Record<string, string> = {};
        if (params.language_id !== undefined && params.language_id !== "") {
            payload.language_id = String(params.language_id);
        }
        if (params.language) {
            payload.language = params.language;
        }
        return this.post("/account/language", payload);
    };

    /** Cached stock review/chat form copy for a locale (`GET /account/widget-copy`). */
    load_widget_copy = async (locale: string): Promise<iWidgetCopyResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/account/widget-copy", {
                withCredentials: true,
                params: { locale, ts: new Date().getTime() },
            })
            .then((res): iWidgetCopyResponse => ({
                statusCode: res.status,
                ...res.data,
            }))
            .catch((error): any => this.handleCatch(error));
    };

    update_profile = async (updateParams: any): Promise<iApiBasicResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        fd.set("first_name", updateParams.first_name);
        fd.set("last_name", updateParams.last_name);
        fd.set("timezone", updateParams.timezone);
        fd.set("email", updateParams.email);
        fd.set("image", updateParams.image);
        fd.set("password", updateParams.password);
        fd.set("password_confirmation", updateParams.password_confirmation);
        if (updateParams.language_id) {
            fd.set("language_id", String(updateParams.language_id));
        }
        if (updateParams.color_scheme) {
            fd.set("color_scheme", updateParams.color_scheme);
        }
        if (updateParams._remove_image) {
            fd.set("_remove_image", "true");
        }
        return axios
            .post(this.getApiBaseURL() + "/account/profile", fd, {
                withCredentials: true,
                headers: {"Content-Type": "multipart/form-data"},
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    update_contact = async (updateParams: iContactUpdateParams): Promise<iApiBasicResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        fd.set("first_name", updateParams.first_name);
        fd.set("last_name", updateParams.last_name);
        fd.set("email", updateParams.email);
        fd.set("address_1", updateParams.address_1);
        fd.set("address_2", updateParams.address_2);
        fd.set("city", updateParams.city);
        fd.set("state", updateParams.state);
        fd.set("zip", updateParams.zip);
        fd.set("url", updateParams.url);
        fd.set("country_id", updateParams.country_id);
        fd.set("company", updateParams.company);
        fd.set("phone", updateParams.phone);
        fd.set("tax_number", updateParams.tax_number);
        fd.set("billing_address", updateParams.billing_address);
        return axios
            .post(this.getApiBaseURL() + "/account/contact", fd, {
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    fetch_online_users = async (): Promise<any> => {
        let data = {
            is_typing: 0
        };
        return axios
            .post(this.getApiBaseURL() + "/users/update_user_activity", data, {
                withCredentials: true
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((): any => ({statusCode: 999, errors: {network: "Network error!"}}))
    };

    emailVerification = async (id:String): Promise<any> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        return axios
            .get(this.getApiBaseURL() + "/email/verification-notification/"+id, {
                withCredentials: true
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((): any => ({statusCode: 999, errors: {network: "Network error!"}}))
    };

    fetch_chat = async (chat_request_id: any): Promise<any> => {
        return axios
            .get(this.getApiBaseURL() + "/message/get_messages/" + chat_request_id + "", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ([...res.data]))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    send_message = async (data: any): Promise<any> => {
        // let data = {
        //     chat_request_id: 0
        // };
        return axios
            .post(this.getApiBaseURL() + "/message/send_message", data, {
                withCredentials: true
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    home_dashboard = async (): Promise<DashboardHomeResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/home_dashboard", {
                withCredentials: true,
                params: {
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    report = async (start_date: string, end_date: string): Promise<iDashboardReportResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/reports", {
                withCredentials: true,
                params: {
                    start_date: start_date,
                    end_date: end_date,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    sales_breakdown = async (query: iListingQuery): Promise<SalesBreakDownResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/sales_breakdown", {
                withCredentials: true,
                params: {
                    ...query,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    daily_order_report = async (start_date: string, end_date: string): Promise<iDashboardHomeAnalytics> => {
        return axios
            .get(this.getApiBaseURL() + "/daily_order_report", {
                withCredentials: true,
                params: {
                    start_date: start_date,
                    end_date: end_date,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    daily_subscriber_report = async (start_date: string, end_date: string): Promise<iSubscriberGraphResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/daily_subscriber_report", {
                withCredentials: true,
                params: {
                    start_date: start_date,
                    end_date: end_date,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    daily_review_report = async (start_date: string, end_date: string): Promise<iReviewStatsResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/daily_review_report", {
                withCredentials: true,
                params: {
                    start_date: start_date,
                    end_date: end_date,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    daily_email_report = async (start_date: string, end_date: string): Promise<SalesBreakDownResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/daily_email_report", {
                withCredentials: true,
                params: {
                    start_date: start_date,
                    end_date: end_date,
                    ts: new Date().getTime()
                }
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    permission = async (): Promise<iPermission> => {
        return axios
            .get(this.getApiBaseURL() + "/account/permission", {
                withCredentials: true,
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
}
